/**
 * ticketprimitive.ts — the draggable entry / SL / TP lines and their R:R bands.
 *
 * WHAT THIS DRAWS
 * ---------------
 * TradingView's order-ticket geometry: a horizontal line per leg with a label
 * plate on the left, a translucent RED band between entry and stop (what you can
 * lose) and a GREEN band between entry and target (what you can make). Grab a
 * line, drag it, and the price follows the cursor.
 *
 * THE SAME RULE AS EVERY OTHER FILE IN web/
 * -----------------------------------------
 * This computes NO risk rule. It converts prices to pixels and pixels back to
 * prices, and it reports a drag to its owner. Every number in a label arrives
 * pre-formatted from the caller, which got it from `/api/trade/preview`, which
 * got its verdicts from `exec_tools.cli.risk_gates`. If this file ever decided
 * that a stop was "too close" or computed an R:R, the chart would hold a second
 * opinion about money — strictly worse than holding none.
 *
 * The one piece of arithmetic below is `coordinateToPrice`, which is the inverse
 * of the projection the chart already owns. That is geometry, not policy.
 *
 * WHY A PRIMITIVE, AND WHY NOT `createPriceLine`
 * ----------------------------------------------
 * `rangeprimitive.ts` already recorded that `createPriceLine` spans the whole
 * pane and cannot fill a band. It also cannot be DRAGGED: there is no hit-test
 * hook on a price line, so the pointer would have to guess which line it was
 * over from its own coordinate maths — a second projection that drifts from the
 * chart's whenever the scale changes.
 *
 * `ISeriesPrimitiveBase.hitTest(x, y)` is the supported hook (verified present
 * in lightweight-charts 4.2.3's typings, which is the version in this app's
 * package-lock), and it lets the LIBRARY decide what the cursor is over using
 * the same coordinate system it drew with. That is why the drag is hosted on a
 * primitive rather than on a div overlay: a div would need to re-derive the
 * price scale, and the two would disagree exactly when it matters (after a zoom).
 *
 * THE DRAG ITSELF NO LONGER LIVES HERE. It was lifted into `legdrag.ts` when the
 * LIVE position's stop and target had to become draggable on all four panes
 * (`levelprimitive.ts`): one gesture, two hosts, no second implementation of
 * "which line is under the cursor and what price is that pixel".
 *
 * WHY DRAGGING MUST DISABLE THE CHART'S OWN SCROLL
 * -----------------------------------------------
 * lightweight-charts pans the time scale on pointer-drag. Without suppressing
 * that for the duration of a leg drag, moving a stop also scrolls the chart
 * sideways — the line appears to stick while the candles slide underneath.
 * `handleScroll`/`handleScale` are toggled off on grab and restored on release,
 * including on a pointer-cancel, because a chart left unable to pan is a broken
 * chart and the failure is silent.
 */
import type {
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  ISeriesPrimitivePaneRenderer,
  ISeriesPrimitivePaneView,
  PrimitiveHoveredItem,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts';

import { drawPlate, drawRule, type PlateBox } from './levelplate';
import { LegDrag, wireLegDrag } from './legdrag';

/** Which leg a line is. `tp` carries an index so a ladder can be dragged. */
export type LegKind = 'entry' | 'stop' | 'tp' | 'liq';

/** One draggable (or read-only) level. Geometry + presentation, no semantics. */
export type TicketLeg = {
  id: string;
  kind: LegKind;
  price: number;
  /** Left-hand plate text. Pre-formatted by the caller — see the file header. */
  label: string;
  color: string;
  dashed: boolean;
  /** False for reference levels (liquidation, mark) that must not be moved. */
  draggable: boolean;
};

/** A shaded risk/reward zone between two prices. */
export type TicketBand = {
  from: number;
  to: number;
  fill: string;
  label: string;
  labelColor: string;
};

export type TicketGeometry = { legs: TicketLeg[]; bands: TicketBand[] };

/**
 * The fancy-canvas rendering scope, structurally re-declared.
 *
 * Same reasoning as `rangeprimitive.ts`: fancy-canvas is lightweight-charts' own
 * rendering dependency, not a direct dependency of this app, so its types are
 * described here rather than imported from a package the manifest does not list.
 */
type Scope = {
  context: CanvasRenderingContext2D;
  horizontalPixelRatio: number;
  verticalPixelRatio: number;
  bitmapSize: { width: number; height: number };
};
type Target = { useBitmapCoordinateSpace: (f: (scope: Scope) => void) => void };

export type TicketCallbacks = {
  /** Live during a drag — fires on every pointer move. */
  onDrag: (id: string, price: number) => void;
  /** Once, on release. The caller re-previews here rather than per frame. */
  onDrop: (id: string, price: number) => void;
};

export class TicketPrimitive implements ISeriesPrimitive<Time> {
  private _chart: IChartApi | null = null;
  private _series: ISeriesApi<SeriesType, Time> | null = null;
  private _requestUpdate: (() => void) | null = null;
  private _data: TicketGeometry = { legs: [], bands: [] };
  private readonly _views: ISeriesPrimitivePaneView[];

  private _cb: TicketCallbacks | null = null;
  private _detach: (() => void) | null = null;
  /** Chart interaction flags saved at grab time, restored on release.
   *
   *  PER PRIMITIVE, and safe only because `legdrag`'s hub gives a gesture to exactly ONE
   *  host (R4-8): this and `LevelPrimitive._savedScroll` both live on the execution pane, and
   *  when both hosts grabbed one pointerdown the second saved the `false` the first had just
   *  written and restored THAT — the chart stopped panning, silently. Only the active host's
   *  copy is ever written now, so these are two charts' flags rather than two states of one. */
  private _savedScroll: unknown = null;
  private readonly _drag = new LegDrag({
    series: () => this._series,
    legs: () => this._data.legs,
    callbacks: () => this._cb,
    setScroll: (on) => {
      const chart = this._chart;
      if (!chart) return;
      if (!on) {
        this._savedScroll = chart.options().handleScroll;
        chart.applyOptions({ handleScroll: false, handleScale: false });
      } else {
        chart.applyOptions({
          handleScroll: (this._savedScroll ?? true) as never,
          handleScale: true,
        });
      }
    },
    requestUpdate: () => this._requestUpdate?.(),
  });

  constructor() {
    this._views = [
      {
        // Bands under the candles so price action stays readable through them —
        // the same layering decision `rangeprimitive.ts` made for OTE fills.
        zOrder: () => 'bottom',
        renderer: (): ISeriesPrimitivePaneRenderer => ({
          draw: () => {},
          drawBackground: (t) => this._drawBands(t as unknown as Target),
        }),
      },
      {
        zOrder: () => 'top',
        renderer: (): ISeriesPrimitivePaneRenderer => ({
          draw: (t) => this._drawLegs(t as unknown as Target),
        }),
      },
    ];
  }

  attached(param: SeriesAttachedParameter<Time, SeriesType>): void {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
    this._wirePointer();
  }

  detached(): void {
    this._detach?.();
    this._detach = null;
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews(): readonly ISeriesPrimitivePaneView[] {
    return this._views;
  }

  /**
   * Make the price scale include the ticket's own levels.
   *
   * This is a CORRECTNESS fix, not a nicety: a level outside the visible price
   * range is not drawn at all (`priceToCoordinate` returns off-pane), so it
   * cannot be seen, cannot be dragged, and — worst of the three — its absence is
   * indistinguishable from "no stop set". Merging the legs into the series'
   * autoscale means a stop is always on screen when the scale is in auto mode.
   *
   * The liquidation leg is EXCLUDED from the range: at low leverage it sits far
   * from entry (42,666 for a 3x long entered at 64,000), and including it would
   * zoom the chart out until the entry/SL/TP cluster collapsed into a few pixels
   * — measured on the rendered page, the exact effect that made the risk/reward
   * bands unreadable. It is still DRAWN when it falls in range; it just does not
   * get a vote on the range.
   */
  autoscaleInfo(): { priceRange: { minValue: number; maxValue: number } } | null {
    const prices = this._data.legs
      .filter((l) => l.kind !== 'liq')
      .map((l) => l.price)
      .filter((p) => Number.isFinite(p) && p > 0);
    if (!prices.length) return null;
    return {
      priceRange: { minValue: Math.min(...prices), maxValue: Math.max(...prices) },
    };
  }

  setCallbacks(cb: TicketCallbacks): void {
    this._cb = cb;
  }

  /** Replace the geometry. `{legs:[],bands:[]}` clears the ticket from the chart. */
  setData(d: TicketGeometry): void {
    this._data = d;
    this._requestUpdate?.();
  }

  /**
   * Hit test, called by the library with CSS (not bitmap) coordinates.
   *
   * Returning a `cursorStyle` is what makes the line feel grabbable — without it
   * the cursor stays a crosshair and nothing suggests the level can be moved.
   * `externalId` is the leg id, so the pointerdown handler does not need to
   * repeat this search.
   */
  hitTest(x: number, y: number): PrimitiveHoveredItem | null {
    // `x` is intentionally unused: the lines span the pane, so proximity in y is
    // the whole test. Kept in the signature because the library supplies it.
    void x;
    const id = this._drag.hover(y);
    if (id === null) return null;
    return { cursorStyle: 'ns-resize', externalId: id, zOrder: 'top' };
  }

  /** Pointer handling on this chart's own canvas — see `legdrag.wireLegDrag`. */
  private _wirePointer(): void {
    if (!this._chart || !this._series) return;
    this._detach = wireLegDrag(this._chart.chartElement(), this._drag);
  }

  private _y(price: number, vr: number): number | null {
    const y = this._series?.priceToCoordinate(price);
    return y == null ? null : y * vr;
  }

  private _drawBands(target: Target): void {
    if (!this._series || !this._data.bands.length) return;
    target.useBitmapCoordinateSpace(({ context: ctx, verticalPixelRatio: vr, bitmapSize }) => {
      for (const b of this._data.bands) {
        const y1 = this._y(b.from, vr);
        const y2 = this._y(b.to, vr);
        if (y1 === null || y2 === null) continue;
        ctx.fillStyle = b.fill;
        ctx.fillRect(0, Math.min(y1, y2), bitmapSize.width, Math.abs(y2 - y1));
      }
    });
  }

  private _drawLegs(target: Target): void {
    if (!this._series) return;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr,
                                     verticalPixelRatio: vr, bitmapSize }) => {
      // Plates are laid out in y ORDER and each remembers the box it took, so a
      // later plate that would collide steps to the RIGHT instead of drawing on
      // top of its neighbour.
      //
      // Measured on the rendered page (BTC 1D, full 0-140k range visible): the
      // entry / SL / TP / LIQ plates all began at x=6px, so with legs a few
      // hundred dollars apart — i.e. the normal case for a real stop — the entry
      // label was completely buried under TP1's and only three of four chips
      // could be read. A label you cannot read is a level you cannot verify.
      const taken: PlateBox[] = [];
      const ordered = this._data.legs
        .map((leg) => ({ leg, y: this._y(leg.price, vr) }))
        .filter((r): r is { leg: TicketLeg; y: number } => r.y !== null)
        .sort((a, b) => a.y - b.y);

      for (const { leg, y } of ordered) {
        const active = this._drag.dragging === leg.id || this._drag.hovered === leg.id;
        drawRule(ctx, y, bitmapSize.width, leg.color, hr, {
          dashed: leg.dashed,
          emphasis: active,
        });
        taken.push(
          drawPlate(ctx, leg.label, leg.color, y, hr, vr, taken, {
            emphasis: active,
            paneHeight: bitmapSize.height / vr,
          }),
        );

      }
      // Band labels last so they are never overdrawn by a line.
      for (const b of this._data.bands) {
        const y1 = this._y(b.from, vr);
        const y2 = this._y(b.to, vr);
        if (y1 === null || y2 === null || !b.label) continue;
        ctx.save();
        ctx.font = `${Math.round(10 * vr)}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.fillStyle = b.labelColor;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'right';
        ctx.fillText(b.label, bitmapSize.width - 8 * hr, (y1 + y2) / 2);
        ctx.restore();
      }
    });
  }

}
